import { types, flow } from 'mobx-state-tree'

import { TRAFFIC_STATUS_MAP, TRAFFIC_STATUS_ORDER, TRAFFIC_UNEDITABLE_STATUS_VALUE } from "~/utilities/constants"
import TRAFFIC_TICKET_VALIDATIONS from '~/utilities/trafficTicketValidations'

import * as TrafficTicketService from '~/services/trafficTicket'

import Attorney from './Attorney'
import PersistedImage from '~/stores/models/persistedImage'
import buildPersistedChangesStore from '~/stores/models/persistedChanges'
import buildCustomValidationStore from '~/stores/models/customValidation'

const TrafficTicketBase = types
  .model('TrafficTicket', {
    // Actual Ticket Stuff
    objectId: types.identifier,
    ticketImage: types.maybeNull(PersistedImage),
    ticketID: types.optional(types.string, ''),
    // status: types.maybeNull(types.optional(types.string, TRAFFIC_STATUS.temp)),
    status: types.maybeNull(types.string),
    trafficViolation: types.maybeNull(types.string, null),
    violationName: types.maybeNull(types.string),
    locationState: types.maybeNull(types.string),
    locationCourt: types.maybeNull(types.string),
    comment: types.maybeNull(types.string),
    points: types.maybeNull(types.number),
    caseProtectionActivated: types.optional(types.boolean, false),
    caseProtectionFee: types.maybeNull(types.number),
    // Prices
    winItPrice: types.maybeNull(types.number),
    basePrice: types.maybeNull(types.number),
    platformFee: types.maybeNull(types.number),
    attorneyPrice: types.maybeNull(types.number),
    // Date
    createdAt: types.maybeNull(types.Date),
    updatedAt: types.maybeNull(types.Date),
    issueDate: types.maybeNull(types.Date),
    hearingDate: types.maybeNull(types.Date),
    dateSubmitted: types.maybeNull(types.Date),
    dateApproved: types.maybeNull(types.Date),
    // Pointers
    user: types.maybeNull(types.string),
    attorney: types.maybeNull(Attorney)
  })
  .preProcessSnapshot(snapshot => {
    if (!snapshot) return null
    // this function will run on a snapshot to format it before submitting it to the store.
    const parsedSnapshot = {}
    const keys = Object.keys(snapshot)
    keys.forEach(key => {
      const input = snapshot[key]
      switch (key) {
        case 'trafficCourt':
        case 'trafficCounty':
        case 'trafficRegion':
        case 'user':
          parsedSnapshot[key] = null
          if (typeof input === 'string') {
            parsedSnapshot[key] = input
          }
          if (input && input.objectId) {
            parsedSnapshot[key] = input.objectId
          }
          break
        case 'ticketImage':
          if (input && input.url && input.name) {
            parsedSnapshot[key] = {}
            parsedSnapshot[key].persistedPointer = {
              name: input.name,
              url: input.url
            }
          }
          break
        case 'createdAt':
        case 'updatedAt':
          parsedSnapshot[key] = input ? new Date(input) : null
          break
        case 'hearingDate':
        case 'issueDate':
        case 'dateSubmitted':
        case 'dateApproved':
          if (input && typeof input === 'string') {
            const timestamp = Date.parse(input)
            if (!isNaN(timestamp)) {
              parsedSnapshot[key] = new Date(input)
            }
          }
          if (input && input.iso) {
            parsedSnapshot[key] = new Date(input.iso)
          }
          break
        case 'attorneyPrice':
        default:
          parsedSnapshot[key] = input
          break
      }
    })

    return parsedSnapshot
  })
  .actions(self => ({
    checkAndFetchEvidenceImages: flow(function* checkAndFetchGen() {
      try {
        if (!self.objectId) return false
        const images = yield TrafficTicketService.getTicketEvidenceImages(
          self.objectId
        )
        if (!images || !images.length) return false
        self.persistedEvidenceImages = images.map(image => ({
          persistedPointer: image
        }))
        const results = yield Promise.all(
          self.persistedEvidenceImages.map(image =>
            image.fetchAlreadyPersistedImage()
          )
        )
        return results
      } catch (err) {
        console.error('Error loading already uploaded evidence images', err)
        return false
      }
    }),
  }))
  .views(self => ({
    get totalPrice() {
      let displayPrice = 0
      if (self.winItPrice) displayPrice += self.winItPrice
      if (self.caseProtectionActivated) {
        if (self.caseProtectionFee) displayPrice += self.caseProtectionFee
      }
      return displayPrice
    },
    get humanStatus() {
      return TRAFFIC_STATUS_MAP[self.status]
    },
    get canSubmit() {
      return TRAFFIC_STATUS_ORDER[self.status] < TRAFFIC_UNEDITABLE_STATUS_VALUE
    }
  }))

const PersistedChangesStore = buildPersistedChangesStore({
  keyTransformer: (self, key) => {
    switch (key) {
      case 'trafficCourt':
      case 'trafficCounty':
      case 'trafficRegion':
        if (self[key] && self[key].objectId) {
          return self[key].objectId
        }
        return self[key]
      default:
        return self[key]
    }
  }
})

const CustomValidationStore = buildCustomValidationStore({
  validationObject: TRAFFIC_TICKET_VALIDATIONS
})

const TrafficTicket = types.compose(
  TrafficTicketBase,
  PersistedChangesStore,
  CustomValidationStore
)
export default TrafficTicket
